import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../../../core/constants/colors';

interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: (otp: string, transactionRef: string) => Promise<void>;
  transactionRef: string;
  isLoading?: boolean;
  error?: string;
}

export const OTPModal: React.FC<OTPModalProps> = ({
  isOpen,
  onClose,
  onValidate,
  transactionRef,
  isLoading = false,
  error,
}) => {
  const [otp, setOtp] = useState('');
  const [localError, setLocalError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setOtp('');
      setLocalError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!otp.trim()) {
      setLocalError('Please enter the OTP code');
      return;
    }

    if (!/^\d{6}$/.test(otp.trim())) {
      setLocalError('OTP must be 6 digits');
      return;
    }

    try {
      await onValidate(otp.trim(), transactionRef);
    } catch (err: any) {
      setLocalError(err.message || 'Failed to validate OTP');
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setOtp(value);
    setLocalError('');
  };

  if (!isOpen) return null;

  const displayError = error || localError;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold" style={{ color: COLORS.primary.persianIndigo }}>
              Verify Payment
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              ×
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4" style={{ backgroundColor: COLORS.user.primaryLight }}>
              <svg
                className="w-8 h-8"
                style={{ color: COLORS.user.primary }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <p className="text-center text-gray-600 mb-2">
              Please enter the 6-digit code sent to your phone number
            </p>
            <p className="text-center text-sm text-gray-500">
              Transaction: <span className="font-mono">{transactionRef}</span>
            </p>
          </div>

          {displayError && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FFEBEE' }}>
              <p className="text-sm" style={{ color: '#C62828' }}>{displayError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                OTP Code
              </label>
              <input
                ref={inputRef}
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder="000000"
                maxLength={6}
                className={`w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 rounded-lg focus:outline-none focus:ring-2 ${
                  displayError
                    ? 'border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                disabled={isLoading}
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: COLORS.user.primary }}
                disabled={isLoading || !otp.trim()}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-sm"
              style={{ color: COLORS.user.primary }}
              onClick={() => {
                // Resend OTP functionality could be added here
                alert('OTP resend functionality can be implemented if needed');
              }}
            >
              Didn't receive code? Resend
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
